import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib';
import {
  Dropdown,
  DropdownTriggerButton,
  DropdownContent,
} from '@/components/Dropdown';

export interface OptionSelectorOption<T extends string | number> {
  value: T;
  title: string;
  description: string;
}

export interface OptionSelectorProps<T extends string | number> {
  options: OptionSelectorOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  className?: string;
}
export function OptionSelector<T extends string | number>({
  options,
  value,
  onChange,
  className,
}: OptionSelectorProps<T>) {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = useCallback(
    (selected: T) => {
      onChange(selected);
      setDropdownOpen(false);
    },
    [onChange]
  );

  if (value === null) {
    return (
      <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-3', className)}>
        {options.map((option) => (
          <OptionCard<T>
            key={option.value}
            option={option}
            isSelected={false}
            onSelect={handleSelect}
          />
        ))}
      </div>
    );
  }

  return (
    <Dropdown open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownTriggerButton className={cn('w-full justify-between py-3 px-4', className)}>
        <div className="flex items-center gap-2">
          <div className="size-4 rounded-full border-2 border-primary flex items-center justify-center">
            <div className="size-2 rounded-full bg-primary" />
          </div>
          <span className="font-medium text-sm text-onsurface">
            {selectedOption?.title ?? t('common.selectOption')}
          </span>
        </div>
        <svg
          className={cn(
            'size-4 text-muted-foreground transition-transform',
            dropdownOpen && 'rotate-180'
          )}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </DropdownTriggerButton>
      <DropdownContent className="p-3 ">
        <div className="space-y-2">
          {options.map((option) => (
            <OptionCard<T>
              key={option.value}
              option={option}
              isSelected={option.value === value}
              onSelect={handleSelect}
              compact
            />
          ))}
        </div>
      </DropdownContent>
    </Dropdown>
  );
}

function OptionCard<T extends string | number>({
  option,
  isSelected,
  onSelect,
  compact = false,
}: OptionCardProps<T>) {
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      className={cn(
        'flex flex-col items-start text-left rounded-lg border transition-all max-w-[300px]',
        compact ? 'p-3' : 'p-4',
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border bg-surface hover:border-primary/50 hover:bg-surface-darker'
      )}
    >
      <div className="flex items-center gap-2 w-full">
        <div
          className={cn(
            'size-4 rounded-full border-2 flex items-center justify-center',
            isSelected ? 'border-primary' : 'border-muted'
          )}
        >
          {isSelected && <div className="size-2 rounded-full bg-primary" />}
        </div>
        <span
          className={cn(
            'font-medium text-sm',
            isSelected ? 'text-primary' : 'text-onsurface'
          )}
        >
          {option.title}
        </span>
      </div>
      <p className={cn('text-xs text-muted-foreground mt-2 ml-6', compact && 'mt-1')}>
        {option.description}
      </p>
    </button>
  );
}



interface OptionCardProps<T extends string | number> {
  option: OptionSelectorOption<T>;
  isSelected: boolean;
  onSelect: (value: T) => void;
  compact?: boolean;
}
